import { useState, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Github, Eye, EyeOff, ArrowLeft, Shield, Zap, Users, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { SEO } from '../components/SEO';
import { API_URL } from '../lib/config';
type Mode = 'login' | 'register';

function set(
  setForm: React.Dispatch<React.SetStateAction<{ email: string; password: string; name: string; orgName: string }>>,
  field: string,
) {
  return (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(f => ({ ...f, [field]: e.target.value }));
}

const BENEFITS = [
  { icon: Zap,    title: 'Instant codebase intelligence', desc: 'Ask questions about any repository in natural language.' },
  { icon: Share2, title: 'Architecture visualization',    desc: 'Interactive service dependency graphs from your code.' },
  { icon: Users,  title: 'Team knowledge sharing',        desc: 'Surface who owns what and who made architectural decisions.' },
  { icon: Shield, title: 'Enterprise-grade security',     desc: 'SOC 2 Type II certified. Your code stays yours.' },
];

export function LoginPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>('login');
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', name: '', orgName: '' });
  const [githubStatus, setGithubStatus] = useState<'idle' | 'waking' | 'slow'>('idle');

  async function handleGithubLogin() {
    setGithubStatus('waking');
    // Free-tier Render instances spin down when idle — a direct window.location.href
    // navigation to a sleeping instance gets intercepted by Render's own splash page
    // instead of our OAuth redirect. Ping /health first (same instance, so this wakes
    // it up) and only navigate once the server actually answers.
    const slowNoticeTimer = setTimeout(() => setGithubStatus('slow'), 6000);
    try {
      await fetch(`${API_URL}/health`);
    } catch {
      // Even a failed fetch means the wake-up request went out — the instance is
      // booting. Proceed to the real redirect either way; worst case the user sees
      // Render's own splash briefly instead of a silent failure.
    } finally {
      clearTimeout(slowNoticeTimer);
      window.location.href = `${API_URL}/auth/github`;
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = mode === 'login' ? '/auth/login' : '/auth/register';
      const body = mode === 'login'
        ? { email: form.email, password: form.password }
        : { email: form.email, password: form.password, name: form.name, orgName: form.orgName };

      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error?.message || 'Authentication failed');

      localStorage.setItem('cp_token', data.data.token);
      localStorage.setItem('cp_user', JSON.stringify(data.data.user));
      toast.success(mode === 'login' ? 'Welcome back!' : 'Account created!');
      navigate('/dashboard');
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: 'var(--s0)' }}>
      <SEO
        title="Sign in"
        description="Sign in to CodePilot Engineer — AI-powered codebase intelligence for software engineering teams."
        url="https://codepilot.engineer/login"
        noIndex
      />
      {/* Left panel — brand & benefits */}
      <div
        className="hidden lg:flex w-[480px] flex-col justify-between p-12 relative overflow-hidden flex-shrink-0 border-r"
        style={{ backgroundColor: 'var(--s1)', borderColor: 'var(--b-subtle)' }}
      >
        <div className="absolute inset-0 bg-dots opacity-60 pointer-events-none" />
        <div className="absolute bottom-0 left-0 right-0 h-64 pointer-events-none"
          style={{ background: 'linear-gradient(to top, var(--overlay-sm), transparent)' }} />

        {/* Logo */}
        <div className="relative">
          <Link to="/" className="inline-flex items-center gap-3 group">
            <img src="/images/codepilotlogo.png" alt="CodePilot Engineer" className="w-9 h-9 rounded-xl object-contain" />
            <div>
              <div className="text-base font-semibold text-t1 tracking-tight">CodePilot Engineer</div>
              <div className="text-xs text-t4">Engineering Intelligence Platform</div>
            </div>
          </Link>
        </div>

        {/* Main copy */}
        <div className="relative space-y-6">
          <div>
            <h2 className="text-3xl font-bold text-t1 tracking-tight leading-tight mb-3 text-balance">
              The operating system for engineering knowledge
            </h2>
            <p className="text-t2 leading-relaxed text-sm">
              Connect your repositories and let CodePilot Engineer become the central knowledge layer for your entire engineering organization.
            </p>
          </div>

          <div className="space-y-4">
            {BENEFITS.map(b => (
              <div key={b.title} className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{ backgroundColor: 'var(--overlay-sm)', border: '1px solid var(--b-default)' }}>
                  <b.icon className="w-3.5 h-3.5" style={{ color: 'var(--t1)' }} />
                </div>
                <div>
                  <div className="text-sm font-medium text-t1">{b.title}</div>
                  <div className="text-xs text-t3 mt-0.5 leading-relaxed">{b.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Testimonial */}
        <div className="relative">
          <div className="card p-4">
            <p className="text-sm text-t2 leading-relaxed mb-3 italic">
              "CodePilot cut our onboarding time from weeks to days. New engineers get up to speed on our 300-service architecture in their first week."
            </p>
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--t1)' }}>
                <span className="text-[11px] font-semibold" style={{ color: 'var(--s0)' }}>SK</span>
              </div>
              <div>
                <div className="text-xs font-medium text-t1">Sarah Kim</div>
                <div className="text-[10px] text-t4">VP Engineering · Acme Corp</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel — auth form */}
      <div className="flex-1 flex flex-col">
        {/* Top nav */}
        <div className="flex items-center justify-between px-8 py-5 border-b" style={{ borderColor: 'var(--b-subtle)' }}>
          <Link to="/" className="flex items-center gap-2 text-sm text-t3 hover:text-t1 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
          <div className="text-sm text-t3">
            {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
            <button onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
              className="font-medium transition-colors hover:opacity-70" style={{ color: 'var(--t1)' }}>
              {mode === 'login' ? 'Sign up' : 'Sign in'}
            </button>
          </div>
        </div>

        {/* Form area */}
        <div className="flex-1 flex items-center justify-center px-8 py-12">
          <div className="w-full max-w-[380px] animate-fade-in">
            <div className="mb-8">
              <h1 className="text-2xl font-bold text-t1 tracking-tight mb-2">
                {mode === 'login' ? 'Sign in to CodePilot' : 'Create your account'}
              </h1>
              <p className="text-sm text-t3">
                {mode === 'login'
                  ? 'Welcome back. Enter your credentials to continue.'
                  : 'Get started free. No credit card required.'}
              </p>
            </div>

            {/* GitHub OAuth */}
            <button
              type="button"
              disabled={githubStatus !== 'idle'}
              onClick={handleGithubLogin}
              className="w-full flex items-center justify-center gap-3 py-2.5 px-4 rounded-lg transition-all text-sm font-medium mb-5 group text-t1 border disabled:cursor-wait"
              style={{ backgroundColor: 'var(--s2)', borderColor: 'var(--b-default)' }}
              onMouseEnter={e => {
                if (githubStatus === 'idle') {
                  e.currentTarget.style.backgroundColor = 'var(--s1)';
                  e.currentTarget.style.borderColor = 'var(--b-strong)';
                }
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = 'var(--s2)';
                e.currentTarget.style.borderColor = 'var(--b-default)';
              }}
            >
              {githubStatus === 'idle' ? (
                <>
                  <Github className="w-4 h-4 group-hover:scale-110 transition-transform" />
                  Continue with GitHub
                </>
              ) : (
                <>
                  <span className="w-4 h-4 border-2 border-t1/30 rounded-full animate-spin" style={{ borderTopColor: 'var(--t1)' }} />
                  {githubStatus === 'slow' ? 'Still waking up the server (free tier, ~30–60s)…' : 'Connecting…'}
                </>
              )}
            </button>

            {/* Divider */}
            <div className="flex items-center gap-3 mb-5">
              <div className="flex-1 h-px" style={{ backgroundColor: 'var(--b-subtle)' }} />
              <span className="text-xs text-t4">or with email</span>
              <div className="flex-1 h-px" style={{ backgroundColor: 'var(--b-subtle)' }} />
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-3">
              {mode === 'register' && (
                <>
                  <div>
                    <label className="block text-xs text-t2 font-medium mb-1.5">Full name</label>
                    <input type="text" placeholder="Jane Doe" value={form.name} onChange={set(setForm, 'name')} required className="input-base" />
                  </div>
                  <div>
                    <label className="block text-xs text-t2 font-medium mb-1.5">Organization</label>
                    <input type="text" placeholder="Acme Corp" value={form.orgName} onChange={set(setForm, 'orgName')} required className="input-base" />
                  </div>
                </>
              )}

              <div>
                <label className="block text-xs text-t2 font-medium mb-1.5">Work email</label>
                <input type="email" placeholder="you@company.com" value={form.email} onChange={set(setForm, 'email')} required autoComplete="email" className="input-base" />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs text-t2 font-medium">Password</label>
                  {mode === 'login' && (
                    <a href="#" className="text-xs transition-colors hover:opacity-70" style={{ color: 'var(--t1)' }}>Forgot password?</a>
                  )}
                </div>
                <div className="relative">
                  <input
                    type={showPw ? 'text' : 'password'}
                    placeholder={mode === 'register' ? 'Min. 8 characters' : ''}
                    value={form.password}
                    onChange={set(setForm, 'password')}
                    required
                    minLength={8}
                    autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                    className="input-base pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(s => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-t3 hover:text-t1 transition-colors"
                  >
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary justify-center py-2.5 mt-2 disabled:opacity-50 disabled:cursor-not-allowed text-[14px]"
              >
                {loading
                  ? <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Please wait…</span>
                  : mode === 'login' ? 'Sign in' : 'Create account'
                }
              </button>
            </form>

            {mode === 'register' && (
              <p className="text-[11px] text-t4 text-center mt-4 leading-relaxed">
                By creating an account you agree to our{' '}
                <a href="#" className="text-t3 hover:text-t1 underline">Terms of Service</a> and{' '}
                <a href="#" className="text-t3 hover:text-t1 underline">Privacy Policy</a>.
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-8 py-4 border-t flex items-center justify-center" style={{ borderColor: 'var(--b-subtle)' }}>
          <div className="flex items-center gap-1.5 text-xs text-t4">
            <Shield className="w-3 h-3" />
            SOC 2 Type II certified · Data encrypted at rest and in transit
          </div>
        </div>
      </div>
    </div>
  );
}
